import type { ReactNode } from 'react'
import { cn } from '@/lib/utils'
import { Breadcrumb, type Crumb } from './breadcrumb'
import { Eyebrow } from './eyebrow'

/**
 * Casca das páginas de CONTEÚDO (blog, glossário, profissões, FAQ, legais).
 *
 * Existe pra acabar com os dois sistemas visuais que conviviam no site: as
 * calculadoras usavam este DS (`max-w-[1000px]`, hairline, título alinhado à
 * esquerda) enquanto o conteúdo usava um legado (`container-app` de 1600px,
 * `PageHero` centralizado, `display-1`). Eram dois produtos na mesma marca.
 *
 * Mesma medida e mesma hierarquia das calculadoras — quem chega pelo blog e
 * clica numa conta não sente que trocou de site.
 */

export function ContentPage({
  children,
  crumbs,
  wide = false,
  className,
}: {
  children: ReactNode
  crumbs?: Crumb[]
  /** Catálogos em grade (glossário, profissões) respiram melhor um pouco mais largos. */
  wide?: boolean
  className?: string
}) {
  return (
    <main className={cn('mx-auto px-5 pt-5 pb-16 sm:px-6', wide ? 'max-w-[1120px]' : 'max-w-[1000px]', className)}>
      {crumbs && <Breadcrumb items={crumbs} />}
      {children}
    </main>
  )
}

export function PageHeader({
  eyebrow,
  title,
  lead,
  children,
  className,
}: {
  eyebrow?: string
  title: ReactNode
  lead?: ReactNode
  /** Ações, chips, busca — o que vier fica logo abaixo do lead. */
  children?: ReactNode
  className?: string
}) {
  return (
    <header className={cn('mb-9 max-w-[680px]', className)}>
      {eyebrow && <Eyebrow className="mb-2.5">{eyebrow}</Eyebrow>}
      <h1 className="font-display text-[clamp(1.75rem,1.4rem+1.4vw,2.15rem)] font-semibold leading-[1.08] tracking-[-0.03em] text-ink">
        {title}
      </h1>
      {lead && <p className="mt-3 text-[16px] leading-relaxed text-ink-mute">{lead}</p>}
      {children && <div className="mt-6">{children}</div>}
    </header>
  )
}

/** Título de seção dentro de uma página de conteúdo. */
export function SectionTitle({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <h2
      className={cn(
        'font-display text-[clamp(1.35rem,1.15rem+0.9vw,1.7rem)] font-semibold tracking-[-0.025em] text-ink',
        className,
      )}
    >
      {children}
    </h2>
  )
}

/** Bloco de destaque no fim da página (CTA de conteúdo). Sóbrio, sem gradiente. */
export function ContentCta({
  title,
  children,
  action,
  className,
}: {
  title: string
  children?: ReactNode
  action?: ReactNode
  className?: string
}) {
  return (
    <section className={cn('mt-14 border border-line bg-brand-faint p-6 sm:p-8', className)}>
      <SectionTitle>{title}</SectionTitle>
      {children && <p className="mt-3 max-w-[620px] text-[15px] leading-relaxed text-ink-mute">{children}</p>}
      {action && <div className="mt-6">{action}</div>}
    </section>
  )
}
